/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
/* eslint-disable max-lines-per-function */
import { type AfterContentChecked, type OnDestroy, type OnInit, ChangeDetectorRef, Component, Input, TemplateRef, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { EventService, I18nService, LazyloadService, LocalStorageService, PermissionService, UtilService } from '@dm/core';
import { type MenuGroup, type MenuItem } from '@dm/core/types';
import { EventServiceKeyE } from 'app/common/constants/event-service-key';
import { LicenseKey } from 'app/common/constants/license-key';
import { AuthService, ToolService } from 'app/common/services';
import { type System } from 'app/common/types/mo';
import { type OEM } from 'app/common/types/oem';
import { ProductConfig, rootMenus, ShardSwitch, SWITCH } from 'app/config';
import { FeatureConfig } from 'app/config/feature';
import { delay } from 'lodash';
import { type Subscription } from 'rxjs';

import { SystemUpgradeComponent } from '../header-settings/upgrade/system-upgrade-panel/system-upgrade.component';
import { UpgradeComponent } from '../header-settings/upgrade/upgrade-panel/upgrade.component';
import { SimpleModeTipComponent } from '../simple-mode-tip/simple-mode-tip.component';
import { SmartAssistantService } from '../smart-assistant/smart-assistant.service';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    host: {
        '[class.small-size]': 'isSmallSize',
        '[class.header-simple]': 'isSimpleMode',
    },
})
export class HeaderComponent implements OnInit, OnDestroy, AfterContentChecked {
    @Input() isSimpleMode: boolean;
    @ViewChild('eServiceContent') eServiceContent: TemplateRef<any>;
    @ViewChild('upgrade') upgrade: UpgradeComponent;
    @ViewChild('systemUpgrade') systemUpgrade: SystemUpgradeComponent;
    @ViewChild('otaUpgrade') otaUpgrade: UpgradeComponent;
    SUPPORT_JA_LAN = FeatureConfig.IsSupportJaLan;

    oem: OEM = (window as any).oem;
    logoUrl = this.oem.vendor === 'huawei' ? undefined : 'assets/images/logo.png';
    // 菜单列表
    menus: MenuItem[] = [];
    // 语言
    isLanguageZh = this.i18nService.language === 'zh';
    language = this.isLanguageZh ? 'English' : '中文';
    // 用户名
    get userName(): string {
        return this.authService.getUserName();
    }
    userTip = this.i18nService.get('service.quota_term_user_lab');
    // 登出
    logoutText = this.i18nService.get('common.logout_label');

    eService = this.i18nService.get('settings.eService_introduce_desc_title');
    // OA白板过滤使用
    eServiceMenu = this.i18nService.get('settings.eService_title');
    eServiceTip = this.i18nService.get('settings.eService_introduce_desc1');
    eServiceContentTip = this.i18nService.get('settings.eService_introduce_desc_info');
    learnMoreTip = this.i18nService.get('settings.eService_introduce_knowMore_btn');
    configNowTip = this.i18nService.get('settings.eService_introduce_configNow_btn');
    selfService = this.i18nService.get('common.eserviceIP_website');
    eServiceOpen = false;
    // OceanProtectX过滤使用
    externalStorageTitle = this.i18nService.get('common.externalStorage_title');

    // 微存储过滤专用
    dataProtectionTitle = this.i18nService.get('common.dataProtection_title');
    vvolTitle = this.i18nService.get('storage.vvol_task_vvol_tittle');
    blockServiceTitle = this.i18nService.get('common.blockStorageService_title');

    // x系列下NAS双活特性过滤使用
    FsHyperMetroFeatures = ['/v-store', '/config/hyper-metro-domain'];

    // 根据组网类型判断是否使能容器
    containerMenu = this.i18nService.get('settings.container_task_setting_title');

    /**
     * 智能助手图标tip
     */
    smartAssistantTooltip = this.i18nService.get('home.smartAssistant_lab');
    /**
     * 是否有LUN的license
     */
    hasLunLicense: boolean;
    /**
     * 是否有二级存储备份的license
     */
    hasDataBackUpCapacityLicense: boolean;
    /**
     * 是否是远程协助管理员
     */
    isRemoteAssistanceUser = false;

    isLogined = false;
    isNotOem = this.oem.vendor === 'huawei';

    // 标识当前是否为登录页
    isNotLoginPage = false;

    isInitializePage = false;

    isPoweronFailPage = false;

    isSmallSize = false;

    showSettings = true;
    showComonTask = true;
    userTooltipVisible = false;
    eServiceTooltipVisible = false;
    moreMenuVisible = false;
    initDone = false;
    hasGuiReadPermission = false;
    hasGuiWritePermission = false;
    isShowEservice = false;

    checkShow = this.permissionService.checkShow;

    // 是否是高端环境
    isAdvance: boolean;

    // 设备名称对象
    deviceNameObj: {
        str: string;
        realStr: string;
        showEllipsis: boolean;
    };
    // 设备信息
    data: System;

    SWITCH = SWITCH;

    ShardSwitch = ShardSwitch;
    FeatureConfig = FeatureConfig;

    get isSuperAdmin(): boolean {
        return this.permissionService.isSuperAdmin();
    }

    /**
     * Dorado/新融合入门级型号/新融合低端型号支持极简模式
     */
    supportSimpleMode: boolean;

    // 前后保留7个字符
    nameLength = 7;

    private subs$: Subscription[] = [];

    // 显示异常检测图标
    showAnomalyIcon = false;
    /**
     * 是否展示极简/标准模式切换提示
     */
    showSimpleModeTip: boolean;
    /**
     * 有智能助手相关的写权限
     */
    hasSmartAssistantWrite: boolean;

    constructor(
        private i18nService: I18nService,
        private authService: AuthService,
        private router: Router,
        private permissionService: PermissionService,
        private eventService: EventService,
        private toolService: ToolService,
        public utilService: UtilService,
        private localStorageService: LocalStorageService,
        private cdref: ChangeDetectorRef,
        private lazyloadService: LazyloadService,
        private smartAssistantService: SmartAssistantService,
    ) { }

    ngOnInit(): void {
        this.menus = rootMenus(this.i18nService, this.authService, this.localStorageService);
        this.watchRouterChange();
        this.oaFilter();
        this.checkDevice();
        this.checkPermission();

        this.eventService.subscribe('systemInfoUpdate', (data) => {
            this.checkDevice();
            this.FeatureMenuSet();
            if (data && data.NAME) {
                this.data = data;
                this.setDeviceName();
            }
        });

        this.eventService.subscribe('internalDeviceInfo', () => {
            // 根据组网类型判断是否展示容器入口
            this.processMenu();
        });

        // License更新后重新设置菜单
        this.eventService.subscribe('license.update', () => {
            this.hasDataBackUpCapacityLicense = this.permissionService.checkShow([], [LicenseKey.DataBackUpCapacity]);
            this.hasLunLicense = this.permissionService.checkShow([], [LicenseKey.SmartThin]);

            // 检查license权限后在对表单项进行更改
            this.processMenu();
        });

        // 远程协助用户
        this.eventService.subscribe(EventServiceKeyE.USER_INFO_CHANGED, () => {
            this.isRemoteAssistanceUser = this.authService.isRemoteAssistanceUser();
        });

        // 全局更新是否部署防勒索标记后，更新菜单
        this.eventService.subscribe('updateAntiransomwareFlag', () => {
            this.processMenu();
        });

        window.addEventListener('resize', this.checkSize);
        window.addEventListener('scroll', () => {
            this.moreMenuVisible = false;
        });
        this.checkSize();
        this.eventService.subscribe('modifyPMSSwitch', (show: boolean) => this.setShowAnomalyIcon(show));
    }

    ngAfterContentChecked(): void {
        this.cdref.detectChanges();
    }

    processMenu(): void {
        this.menus = rootMenus(this.i18nService, this.authService, this.localStorageService);
        this.oaFilter();
        this.FeatureMenuSet();
    }

    displayMenusChange(displayMenus: MenuItem[]): void {
        // 菜单中无数据保护一级菜单时，隐藏高级搜索中数据保护快捷入口
        SWITCH.IS_HIDE_PROTECTION = !displayMenus?.some((item) => item.name === this.i18nService.get('common.dataProtection_title'));
    }

    setDeviceName(): void {
        const nameStr = `${this.data.NAME}${this.data.LOCATION ? '(' + this.utilService.decodeHTML(this.data.LOCATION) + ')' : ''}`;
        this.deviceNameObj = this.toolService.getEllipsisStr(nameStr, this.nameLength);
    }

    /**
     * 查询系统权限
     */
    checkPermission(): void {
        this.hasLunLicense = this.permissionService.checkShow([], [LicenseKey.SmartThin]);
        this.hasDataBackUpCapacityLicense = this.permissionService.checkShow([], [LicenseKey.DataBackUpCapacity]);
        this.initPermission();
        this.subs$.push(
            this.eventService.subscribe('permission.update', () => {
                // 智能GUI有无系统的读权限
                this.initPermission();
            }),
        );
    }

    /**
     * 初始化系统权限
     */
    initPermission(): void {
        this.hasGuiReadPermission = this.permissionService.hasPermission('system_R');
        this.hasGuiWritePermission = this.permissionService.hasPermission('system_W');
        this.hasSmartAssistantWrite = this.smartAssistantService.checkPermission();
    }

    /**
     * 浏览器尺寸发生变化
     */
    checkSize = (): void => {
        if (window.innerWidth >= 1720) {
            this.moreMenuVisible = false;
        }
        // 这加个异步先关闭tooltip,再隐藏
        setTimeout(() => {
            // 检测浏览器宽度是否小于1720，小于1720时菜单栏需要重新排列
            this.isSmallSize = window.innerWidth < 1720;
            if (this.isSmallSize) {
                document.body.classList.add('header-small-size');
            } else {
                document.body.classList.remove('header-small-size');
            }
        }, 0);

        // 浏览器宽度小于1680时设备名称前后保留6个字符，大于时保留7个字符
        if (window.innerWidth < 1680) {
            this.nameLength = 6;
        } else {
            this.nameLength = 7;
        }
        if (this.data && this.data.NAME) {
            this.setDeviceName();
        }
    };

    checkDevice = (): void => {
        this.supportSimpleMode = ProductConfig.IsSupportSimpleMode && !SWITCH.IS_VSTORE_SYSTEM;
        this.isAdvance = FeatureConfig.IsShowHeaderHighProdList;
    };

    /**
     * 监听路由变化选中菜单
     */
    watchRouterChange(): void {
        this.router.events.subscribe((evt: any) => {
            if (evt instanceof NavigationEnd) {
                /** SAML协议下走到login/sso为已鉴权状态，头部菜单栏未获取到权限提前渲染，导致菜单项展示不足，故加此判断 */
                this.isNotLoginPage = window.location.pathname !== '/login' && window.location.pathname !== '/login/sso';
                this.isInitializePage = window.location.pathname === '/initialize';
                this.isPoweronFailPage = window.location.pathname === '/poweronfail';
                this.isLogined = this.authService.isAuth();

                if (this.userTooltipVisible) {
                    this.userTooltipVisible = false;
                }
                if (this.eServiceTooltipVisible) {
                    this.eServiceTooltipVisible = false;
                }

                this.isShowEservice = this.isNotOem &&
                    this.isNotLoginPage &&
                    !this.isInitializePage &&
                    !this.isPoweronFailPage &&
                    this.checkShow(['call_home_R']);

                this.checkShowTip();
            }
        });
    }

    /**
     * 检查是否需要显示极简/标准模式切换Tip
     */
    checkShowTip(): void {
        // 入门级存储首次登陆才显示Tip
        this.showSimpleModeTip = this.supportSimpleMode &&
            this.isNotLoginPage &&
            !this.isInitializePage &&
            this.isLogined &&
            !this.isPoweronFailPage &&
            this.authService.getShowSimpleModeTip() &&
            !this.smartAssistantService.getUserHideTip() &&
            !SWITCH.IS_VSTORE_SYSTEM;

        if (!this.showSimpleModeTip) {
            return;
        }

        // 需要先判断极简模式是否已切换，再延时加载Tip
        if (window.location.pathname.startsWith('/simple')) {
            let timer = setInterval(() => {
                const assistantEl = document.querySelector('app-smart-assistant-switch');
                if (assistantEl) {
                    clearInterval(timer);
                    timer = null;
                    delay(() => {
                        this.lazyloadService.appendComponent(SimpleModeTipComponent);
                    }, 300);
                }
            }, 50);

            return;
        }

        delay(() => {
            this.lazyloadService.appendComponent(SimpleModeTipComponent);
        }, 300);
    }

    /**
     * 修改语言
     */
    changeLan(): void {
        this.i18nService.changeLanguage(this.i18nService.language === 'zh' ? 'en' : 'zh');
    }

    /**
     * 注销
     */
    onLogout(): void {
        this.authService.logout();
        // 租户系统退出登录删除session
        this.localStorageService.remove('vstoreSystemId');
    }

    /**
     * LOGO点击事件，
     * 登录页点击不跳转
     */
    logoClick = (): void => {
        if (!this.isNotLoginPage || this.isInitializePage || this.isPoweronFailPage) {
            return;
        }
        const redirctUrl = SWITCH.IS_VSTORE_SYSTEM ? '/vstore/home' : '/home';
        this.router.navigate([redirctUrl]);
    };

    /**
     * OA白板过滤
     */
    oaFilter(): void {
        // 微存储屏蔽vvol、数据保护
        if (FeatureConfig.IsHideProtection) {
            this.filterMenu(this.vvolTitle);
            this.filterMenu(this.dataProtectionTitle);
        }

        // 微存储屏蔽外部存储
        if (FeatureConfig.IsHideExternalStorage) {
            this.filterMenu(this.externalStorageTitle);
        }
        if (this.oem.vendor !== 'huawei') {
            this.filterMenu(this.eServiceMenu);
        }
    }

    filterMenu(menuName: string): void {
        this.menus = this.menus.filter((menu: MenuItem) => {
            if (menu.name === menuName) {
                return false;
            }
            if (menu.children) {
                menu.children = menu.children.filter((menuGroup) => {
                    if (!menuGroup.children || !menuGroup.children.length) {
                        return false;
                    }
                    menuGroup.children = menuGroup.children.filter((subMenuItem) => subMenuItem.name !== menuName);
                    return menuGroup.children.length > 0;
                });
                return menu.children.length > 0;
            }
            return this.menus;
        });
    }

    /**
     * 特殊场景处理menu
     */
    FeatureMenuSet(): void {
        this.menus.forEach((menu: MenuItem) => {
            if (menu.children) {
                menu.children.forEach((menuGroup: MenuGroup) => {
                    // X系列文件CDP,外部存储通过license判断
                    if (FeatureConfig.IsNeedPermissionAndLicenseBeforeShowExternalStorage) {
                        menuGroup.children.forEach((subMenuItem) => {
                            if (subMenuItem.name === this.externalStorageTitle) {
                                subMenuItem.show = [{
                                    permission: ['remote_device_R'],
                                    license: [LicenseKey.SmartVirtualization],
                                }];
                            }
                        });
                    }

                    // x系列屏蔽多租户和双活域
                    if (!FeatureConfig.IsShowProtectVstore) {
                        menuGroup.children = menuGroup.children.filter(
                            (subMenuItem) => !this.FsHyperMetroFeatures.includes(subMenuItem.url),
                        );
                    }
                });
            }
        });

        // 二级存储有数据备份license时，屏蔽防勒索;非X系列的二级存储，屏蔽防勒索,远程协助管理员屏蔽防勒索
        if (!FeatureConfig.IsSupportHyperDetect ||
            (!FeatureConfig.IsSupportHyperDetectWhenHasDataBackUpCapacityLicense && this.hasDataBackUpCapacityLicense)) {
            this.menus.forEach((menu: MenuItem) => {
                if (menu.children) {
                    menu.children.forEach((menuGroup: MenuGroup) => {
                        menuGroup.children = menuGroup.children.filter(
                            (subMenuItem) => subMenuItem.name !== this.i18nService.get('common.HyperDetect_title'),
                        );
                    });
                }
            });
        }

        // 计算型存储屏蔽FC网络
        if (!FeatureConfig.IsSupportFC) {
            this.menus.forEach((menu: MenuItem) => {
                if (menu.children) {
                    menu.children.forEach((menuGroup: MenuGroup) => {
                        menuGroup.children = menuGroup.children.filter(
                            (subMenuItem) => subMenuItem.name !== this.i18nService.get('system.network_fc_tiitle'),
                        );
                    });
                }
            });
        }

        // 计算型存储和入门级存储屏蔽RoCE网络
        if (!FeatureConfig.IsSupportNVMEOverRoCENetWorkAndLif) {
            this.menus.forEach((menu: MenuItem) => {
                if (menu.children) {
                    menu.children.forEach((menuGroup: MenuGroup) => {
                        menuGroup.children = menuGroup.children.filter(
                            (subMenuItem) => subMenuItem.name !== this.i18nService.get('system.network_roce_title'),
                        );
                    });
                }
            });
        }

        // CSI插件支持挂载PV/pod的型号才支持容器存储
        if (!FeatureConfig.IsSupportContainerStorage) {
            this.menus.forEach((menu: MenuItem) => {
                if (!menu.children || menu.children?.length === 0) {
                    return;
                }
                menu.children.forEach((menuGroup: MenuGroup) => {
                    menuGroup.children = menuGroup.children.filter(
                        (subMenuItem) =>
                            subMenuItem.name !== this.i18nService.get('storage.containerStorage_task_pv_title') &&
                            subMenuItem.name !== this.i18nService.get('storage.containerStorage_task_pod_title'),
                    );
                });
            });
        }
    }

    /**
     * 跳转到设备详情页面
     */
    jumpToDeviceSetting(): void {
        if (SWITCH.IS_VSTORE_SYSTEM) {
            return;
        }
        this.router.navigate(['/settings/basic-infomation/device-info']);
    }

    queryEservice(): void {
        this.eServiceOpen = true;
        this.moreMenuVisible = false;
    }

    openEservice(): void {
        this.router.navigate(['/settings/e-service']);
        this.eServiceOpen = false;
    }

    ngOnDestroy(): void {
        for (const sub$ of this.subs$) {
            sub$.unsubscribe();
        }
    }

    showMoreMenu(): void {

    }

    setShowAnomalyIcon(show: boolean): void {
        // 需要的开关
        const switchTemp = !SWITCH.IS_VSTORE_SYSTEM && SWITCH.SUPPORT_ABNORMAL_DETECTION && SWITCH.PRODUCT_SUPPORT_AI;

        this.showAnomalyIcon = switchTemp && show;
    }
}
